import { Box, Card, DialogContentText, Typography } from '@mui/material'
import { combineMorphotypePoints } from 'components/cell-visualizations/utils'
import { DeepcellPrimaryButton } from 'components/shared'
import ContentLoading from 'components/shared/ContentLoading'
import DeepcellDialog from 'components/shared/DeepcellDialog'
import useFlagCondition from 'components/shared/useFlagCondition'
import React, { useEffect, useState } from 'react'
import { DifferentialFeaturesComparison, PinnedMorphotype, ProjectionMethod } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { useXGBoostAccess } from 'utils/useXGBoostAccess'
import { EditPinnedNamePopover } from './EditPinnedNamePopover'
import { PinnedGroupContent } from './PinnedGroupContent'
import { TrainClassifierButton } from './TrainClassifierButton'

interface PinnedGroupsProps {
  onMergeClick?: (pcg: PinnedMorphotype) => void
  onDifferentialFeaturesClick?: () => void
  deleteComparisonIfIncludesMorphotype?:(comparison:DifferentialFeaturesComparison,morphotypeId:number)=>void
}

export const PinnedGroups = ({
  onMergeClick,
  onDifferentialFeaturesClick,
  deleteComparisonIfIncludesMorphotype
}: PinnedGroupsProps): JSX.Element => {
  const demoEnabled = useFlagCondition('demoEnabled')
  const hasXGBoostAccess = useXGBoostAccess();

  const {
    cellVisualizations: {
      mergedPinnedCells: originalMergedPinnedCells,
      differentialFeaturesComparisons,
      sessionData,
      projection_params: projectionParams,
    },
    visibleMergedPinnedCells: mergedPinnedCells,
    visiblePinnedCells: pinnedCells,
    setMergedPinnedCells,
    deletePinnedGroup,
  } = useCellVisualizationsSlice()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [targetPinnedCell, setTargetPinnedCell] = useState<PinnedMorphotype>()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const eventsManager = useEventsManager()
  const [projectionModelId, setProjectionModelId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Show train classifier only if this is a session meets the following criteria:
  // - We're not in the demo mode
  // - The session was not generated from a TSV
  //     - i.e. we either have a umap-based projection model (in legacy sessions without projection params)
  //     - or we have projection parameters from session creation
  // - For tSNE sessions, require XGBoost access (hide the button)
  const hasValidProjectionForTraining =
    (!!sessionData?.projection_model_id && sessionData?.projection_model_id?.indexOf('cell_vis_umap') >= 0) ||
    projectionParams?.method === ProjectionMethod.UMAP ||
    (projectionParams?.method === ProjectionMethod.TSNE && hasXGBoostAccess)

  const showTrainClassifier = !demoEnabled && hasValidProjectionForTraining

  let differentialFeatureCount = 0;

  const handleChangeIconClick =
    (pinnedMorphotype: PinnedMorphotype) => (e: React.MouseEvent<HTMLElement>) => {
      setTargetPinnedCell(pinnedMorphotype)
      setAnchorEl(e.currentTarget)
    }

  const handleDeleteIconClick = (pinnedMorphotype: PinnedMorphotype) => () => {
    setTargetPinnedCell(pinnedMorphotype)
    setOpenDeleteDialog(true)
  }

  const closeDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setTargetPinnedCell(undefined)
  }

  const handleDeleteConfirm = () => {
    if (targetPinnedCell) {
      deletePinnedGroup({ targetPinnedCellId: targetPinnedCell.id, eventsManager })
      if (originalMergedPinnedCells)
        setMergedPinnedCells({
          pinnedCells: originalMergedPinnedCells.filter((x) => x.id !== targetPinnedCell.id),
          eventsManager,
        })
    }  if (
      differentialFeatureCount > 0 &&
      differentialFeaturesComparisons &&
      differentialFeaturesComparisons.length > 0 &&
      targetPinnedCell
    ) {
      const { id } = targetPinnedCell
      if (deleteComparisonIfIncludesMorphotype) {
        differentialFeaturesComparisons.some((comparison) => {
          deleteComparisonIfIncludesMorphotype(comparison, id)
          return false
        })
      }
    }
  closeDeleteDialog()
  }

  // if seperate pinnedGroups have the same id, they have been merged and should be shown here as one group
  // TODO: This code hurts my eyes. Make it butter? Maybe use Immer?
  const mergedPinnedMorphotypes = (() => {
    // First, try to get merged points from mergedPinnedCells
    const mergedPointsMap = new Map<number, NonNullable<PinnedMorphotype['cells']['points']>>()
    mergedPinnedCells?.forEach((merged) => {
      if (merged.cells.points) {
        mergedPointsMap.set(merged.id, merged.cells.points)
      }
    })

    return pinnedCells?.reduce((acc, pinnedMorphotype) => {
      const existingPinnedMorphotypeIndex = acc.findIndex((x) => x.id === pinnedMorphotype.id)
      if (existingPinnedMorphotypeIndex > -1) {
        return acc.map((accPinnedMorphotype, i) => {
          if (i === existingPinnedMorphotypeIndex) {
            // Try to get merged points first
            const mergedPoints = mergedPointsMap.get(accPinnedMorphotype.id)
            if (mergedPoints) {
              return {
                ...accPinnedMorphotype,
                cells: {
                  ...accPinnedMorphotype.cells,
                  points: mergedPoints,
                },
              }
            }

            // Fallback: combine points from all morphotypes with the same ID
            // This uses the shared utility function to match the logic in MergePinned.tsx handleMerge
            const uniquePoints = combineMorphotypePoints(
              pinnedCells,
              mergedPinnedCells,
              accPinnedMorphotype.id,
              { shuffle: false }
            )

            return {
              ...accPinnedMorphotype,
              cells: {
                ...accPinnedMorphotype.cells,
                points: uniquePoints.length > 0 ? uniquePoints : accPinnedMorphotype.cells.points,
              },
            }
          }
          return accPinnedMorphotype
        })
      }
      return [...acc, pinnedMorphotype]
    }, [] as PinnedMorphotype[])
  })()



  const enableMergeButton = mergedPinnedMorphotypes && mergedPinnedMorphotypes?.length > 1
  if (
    differentialFeaturesComparisons &&
    differentialFeaturesComparisons.length > 0 &&
    targetPinnedCell
  ) {
    const { id } = targetPinnedCell
    differentialFeatureCount = differentialFeaturesComparisons.reduce((acc, comparison) => {
      if (comparison.morphotypeIds1.includes(id) || comparison.morphotypeIds2.includes(id)) {
        return acc + 1
      }
      return acc
    }, 0)
  }

  useEffect(() => {
    if (sessionData) {
      setProjectionModelId(sessionData?.projection_model_id ?? null)
      setIsLoading(false)
    }
  }, [sessionData])

  return (
    <>
      <Card elevation={0}>
        {isLoading && <ContentLoading size={30} sx={{ marginBottom: 1 }} />}
        {showTrainClassifier && projectionModelId && (
          <Box sx={{ marginBottom: 2 }}>
            <TrainClassifierButton />
          </Box>
        )}
        <Box display="flex" justifyContent="center" sx={{ marginBottom: 2 }}>
          <DeepcellPrimaryButton
            outlined
            sx={{ width: '66%' }}
            onClick={onDifferentialFeaturesClick}
          >
            Differential Morphology Features
          </DeepcellPrimaryButton>
        </Box>
        {mergedPinnedMorphotypes?.map((pinnedMorphotype) => (
          <PinnedGroupContent
            key={pinnedMorphotype.id}
            pinnedMorphotype={pinnedMorphotype}
            onChange={handleChangeIconClick(pinnedMorphotype)}
            onDelete={handleDeleteIconClick(pinnedMorphotype)}
            onMerge={() => onMergeClick && onMergeClick(pinnedMorphotype)}
            enableMerge={enableMergeButton}
          />
        ))}
      </Card>
      <EditPinnedNamePopover
        anchorEl={anchorEl}
        pinnedCell={targetPinnedCell ?? { id: -1, name: '', cells: {} }}
        onClose={() => setAnchorEl(null)}
      />

      <DeepcellDialog
        open={openDeleteDialog}
        handleConfirm={handleDeleteConfirm}
        handleCancel={closeDeleteDialog}
        okLabel="Delete"
        cancelLabel="Cancel"
        titleLabel="Confirm Morphotype Deletion"
      >
        {differentialFeatureCount > 0 ? (
          <>
            <DialogContentText>
              {`You are about to delete morphotype ${targetPinnedCell?.name} This will also invalidate and remove associated ${differentialFeatureCount} differential feature comparisons`}
              <Typography sx={{ textAlign: 'center' }}>Do you want to proceed ?</Typography>
            </DialogContentText>
          </>
        ) : (
          <DialogContentText>
            {`Are you sure you want to delete ${targetPinnedCell?.name}?`}
          </DialogContentText>
        )}
      </DeepcellDialog>
    </>
  )
}

export default PinnedGroups